import React from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet';
import { Slider } from 'components/ui/slider';
import { Button } from 'components/ui/button';
import { MapPin as MapPlaceholderIcon, RadioTower } from 'lucide-react';
import { formatTimeWithSeconds } from './fleetTrackingHelpers';
import { redIcon } from './fleetTrackingHelpers';

// Helper to fit map bounds to markers
const FitBoundsToMarkers: React.FC<{
  bounds?: any;
  isLive: boolean;
}> = ({ bounds, isLive }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!isLive && bounds && map) {
      try {
        map.fitBounds(bounds, { padding: [50, 50] });
      } catch (e) {
        console.error('Error fitting bounds:', e);
      }
    }
  }, [bounds, map, isLive]);
  return null;
};

// Helper to auto-center map on vehicle position in live mode
const MapAutoCenter: React.FC<{
  center: any;
  isLive: boolean;
}> = ({ center, isLive }) => {
  const map = useMap();
  React.useEffect(() => {
    if (isLive && center) {
      map.panTo(center, { animate: true, duration: 1 });
    }
  }, [center, isLive, map]);
  return null;
};

// Props for FleetMap
export interface FleetMapProps {
  mapCenter: any;
  mapMarkers: any[];
  plannedRoutePolyline: any[];
  truckPathPolyline: any[];
  mapBounds?: any;
  vehiclePosition: any;
  isFollowingLive: boolean;
  locationIndex: number;
  vehicleLocations: any[];
  handleSliderChange: (v: number[]) => void;
  handleSliderCommit: (v: number[]) => void;
  setIsFollowingLive: (v: boolean) => void;
}

// Map component for displaying vehicle and delivery locations
const FleetMap: React.FC<FleetMapProps> = ({
  mapCenter,
  mapMarkers,
  plannedRoutePolyline,
  truckPathPolyline,
  mapBounds,
  vehiclePosition,
  isFollowingLive,
  locationIndex,
  vehicleLocations,
  handleSliderChange,
  handleSliderCommit,
  setIsFollowingLive,
}) => {
  const hasMapData =
    mapMarkers.length > 0 || (vehicleLocations && vehicleLocations.length > 0);

  return (
    <div className="flex-1">
      {vehicleLocations && vehicleLocations.length > 1 && (
        <div className="bg-gray-50 p-3 rounded-md mb-4 border">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-gray-600 w-28 text-center">
              {formatTimeWithSeconds(
                vehicleLocations[locationIndex]?.timestamp,
              )}
            </span>
            <Slider
              value={[locationIndex]}
              max={vehicleLocations.length - 1}
              step={1}
              onValueChange={handleSliderChange}
              onValueCommit={handleSliderCommit}
              className="flex-1"
            />
            <Button
              variant={isFollowingLive ? 'secondary' : 'default'}
              size="sm"
              onClick={() => setIsFollowingLive(true)}
              disabled={isFollowingLive}
            >
              <RadioTower className="h-4 w-4 mr-2" />
              Suivi direct
            </Button>
          </div>
        </div>
      )}
      {hasMapData ? (
        <MapContainer
          center={mapCenter}
          zoom={10}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%', borderRadius: '8px' }}
          className="min-h-[400px] lg:min-h-0"
        >
          <TileLayer
            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {mapMarkers.map(marker => (
            <Marker
              key={marker.id}
              position={marker.position}
              icon={marker.icon}
            >
              <Popup>
                <span dangerouslySetInnerHTML={{ __html: marker.popupText }} />
              </Popup>
            </Marker>
          ))}
          {vehiclePosition && (
            <Marker position={vehiclePosition.position} icon={redIcon}>
              <Popup>
                Position du véhicule
                <br />à {formatTimeWithSeconds(vehiclePosition.time)}
              </Popup>
            </Marker>
          )}
          {plannedRoutePolyline.length > 0 && (
            <Polyline
              positions={plannedRoutePolyline}
              color="#007bff"
              weight={3}
              opacity={0.6}
              dashArray="5, 10"
            />
          )}
          {truckPathPolyline.length > 0 && (
            <Polyline
              positions={truckPathPolyline}
              color="orangered"
              weight={4}
              opacity={0.8}
            />
          )}
          <FitBoundsToMarkers bounds={mapBounds} isLive={isFollowingLive} />
          <MapAutoCenter
            center={vehiclePosition?.position || null}
            isLive={isFollowingLive}
          />
        </MapContainer>
      ) : (
        <div className="h-full border bg-gray-50 rounded-lg flex flex-col items-center justify-center text-gray-500 p-4 min-h-[400px] lg:min-h-0">
          <MapPlaceholderIcon className="h-16 w-16 mb-3 text-gray-400" />
          <p className="font-medium">Carte non disponible</p>
          <p className="text-sm text-center">
            Aucune donnée de localisation (arrêts ou suivi GPS) n'est disponible
            pour cette tournée.
          </p>
        </div>
      )}
    </div>
  );
};

export default FleetMap;
